import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { Input } from '../../basic/Input/Input';
import { PasswordInput } from '../../basic/Input/PasswordInput';
import styles from './FormField.module.css';

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  hintVariant?: 'default' | 'error';
  children?: ReactNode;
  preventSpaces?: boolean;
}

type PasswordInputCompatibleProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'value' | 'defaultValue'
> & {
  value?: string;
  defaultValue?: string;
};

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, error, hint, hintVariant, required, id, children, ...inputProps }, ref) => {
    const generatedId = useId();
    const fieldId = id || generatedId;
    const errorId = `${fieldId}-error`;
    const hintId = `${fieldId}-hint`;

    const { size, type, value, defaultValue, ...restInputProps } = inputProps;
    const validSize = size && typeof size === 'string' && ['sm', 'md', 'lg'].includes(size)
      ? (size as 'sm' | 'md' | 'lg')
      : 'md';

    const isPassword = type === 'password';
    const passwordValue = typeof value === 'string' ? value : undefined;
    const passwordDefaultValue = typeof defaultValue === 'string' ? defaultValue : undefined;

    return (
      <div className={styles.field}>
        {children || (isPassword ? (
          <PasswordInput
            ref={ref}
            id={fieldId}
            label={label}
            size={validSize}
            value={passwordValue}
            defaultValue={passwordDefaultValue}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            aria-invalid={!!error}
            required={required}
            {...(restInputProps as PasswordInputCompatibleProps)}
          />
        ) : (
          <Input
            ref={ref}
            id={fieldId}
            label={label}
            hasError={!!error}
            size={validSize}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            aria-invalid={!!error}
            required={required}
            type={type}
            value={value}
            defaultValue={defaultValue}
            {...restInputProps}
          />
        ))}
        {hint && !error && (
          <span id={hintId} className={`${styles.hint} ${hintVariant === 'error' ? styles.error : ''}`}>
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className={styles.error} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
